import { deepCompareEQ } from '@cf-shared/deep-helpers';
import { parseRevGeneration } from './sync-rev-and-time-utils';

// How this app compares two revisions of the same document, and what it decides when they
// disagree: whether a body about to be saved is from the past, whether a cached copy is out
// of date, whether replacing it would destroy content, and how loudly to report a merge.
//
// They live outside DatabaseWrapper because that class imports pouchdb-browser, which
// references `self` and cannot be loaded under node, so the logic-heavy parts are extracted
// here and unit-tested directly. DatabaseWrapper delegates to them.

/** How the revision on a body about to be saved compares with the one the app expected (#4577). */
export enum SaveRevMismatch {
  /** the body carries exactly the revision the app expected — the ordinary case */
  Match = 'match',
  /** the body carries a NEWER revision than the app expected, so what the app expected is out of date */
  BodyNewer = 'body-newer',
  /**
   * the body carries an OLDER revision than the app expected: it is a copy from
   * the past, and saving it whole would write whatever it has been carrying over
   * everything that changed since.
   */
  BodyStale = 'body-stale',
}

/**
 * Compare the revision on a body about to be saved with the revision the app
 * expected that document to be at (#4577).
 *
 * A missing or unparseable revision counts as generation 0, which makes it older
 * than any real revision. That is deliberate: a body that has lost its revision
 * is exactly the shape that must not be written through unchecked, and treating
 * it as stale routes it into the merge instead.
 */
export function classifySaveRevMismatch(
  expectedRev: string | null | undefined,
  bodyRev: string | null | undefined
): SaveRevMismatch {
  if (!expectedRev || bodyRev === expectedRev) {
    return SaveRevMismatch.Match;
  }
  const expectedGeneration = parseRevGeneration(expectedRev);
  const bodyGeneration = parseRevGeneration(bodyRev);
  if (bodyGeneration > expectedGeneration) {
    return SaveRevMismatch.BodyNewer;
  }
  return SaveRevMismatch.BodyStale;
}

/**
 * Whether a remembered database body may be used as the starting point for a
 * three-way merge of `record` (#4577).
 *
 * The remembered body IS the database's content at the revision it carries. So
 * it is the version `record` started from only when the two carry the SAME
 * revision. When they differ, it is some other version — and using it anyway is
 * actively harmful rather than merely useless: the merge would decide that every
 * field where the record is behind was deliberately changed by this device, and
 * hand every one of them to the out-of-date copy.
 *
 * That is not hypothetical. A version arriving from the server is remembered as
 * the database's content, so after any sync the remembered body is NEWER than a
 * copy some page has been holding. Without this check, saving that page's copy
 * would overwrite the arriving version wholesale and report that nothing was
 * lost.
 *
 * Compare the whole revision string, never just its generation number: two
 * revisions of the same generation with different hashes are branches that
 * disagree, and one is not the ancestor of the other.
 *
 * Fails closed — a missing revision on either side means unusable.
 */
export function canUseRememberedMergeBase(
  baseRev: string | null | undefined,
  recordRev: string | null | undefined
): boolean {
  if (!baseRev || !recordRev) return false;
  return baseRev === recordRev;
}

/**
 * Whether a remembered database body may be the starting point of the PULL-side
 * three-way merge of an arriving version (#4577).
 *
 * The remembered body is at this device's OWN revision: its last save, or the last
 * arrival it applied. It can only be the version both sides started from when the
 * arriving version is a strictly later generation. An arriving SIBLING (the same
 * generation, a different hash) shares no ancestor this device still holds, so
 * merging against this device's own tip reads every field this device saved on that
 * tip as "unchanged" and hands it to the other branch. The caller then hands the
 * body back unmerged and the identity map keeps this device's branch
 * (KeepSharedContentOverSibling), as it does for every other document.
 *
 * A missing revision on either side answers true, the opposite of
 * canUseRememberedMergeBase. There, "no base" falls back to a two-way merge of a
 * live save. Here, "no fold" means the arriving body replaces the shared object
 * outright, which is the #4577 loss itself, so the fold is kept. The pull never
 * delivers a body without a revision, so that case is theory.
 */
export function canFoldAgainstRememberedBase(
  baseRev: string | null | undefined,
  incomingRev: string | null | undefined
): boolean {
  if (!baseRev || !incomingRev) return true;
  return parseRevGeneration(baseRev) < parseRevGeneration(incomingRev);
}

/**
 * The revisions an arriving document was built on, nearest first, or null when the
 * document does not say (#6404).
 *
 * A document the replicator fetched carries PouchDB's `_revisions` (`start` is the
 * arriving generation, `ids` its hash followed by each ancestor's). A body from a
 * `_changes` row, an `allDocs` read or the conflict resolver carries none, and for
 * those nothing can be said about what they were built on. The arriving revision
 * itself is left out: the question asked of this list is what came BEFORE it.
 */
export function ancestryRevsOf(
  incoming: { _revisions?: { start?: number; ids?: string[] } } | null | undefined
): string[] | null {
  const revisions = incoming?._revisions;
  if (!revisions || typeof revisions.start !== 'number' || !Array.isArray(revisions.ids)) return null;
  const ancestry: string[] = [];
  // for each hash after the arriving one: the generation counts down from `start`
  for (let i = 1; i < revisions.ids.length; i++) {
    ancestry.push(`${revisions.start - i}-${revisions.ids[i]}`);
  }
  return ancestry;
}

/** How the starting version for a pull-side merge was chosen. See `chooseFoldBase`. */
export enum FoldBaseChoice {
  /** the arriving version was built on the body this device remembers as its latest */
  OwnTip = 'own-tip',
  /** it was built on another branch, and an earlier remembered body is on both */
  OlderCommonAncestor = 'older-common-ancestor',
  /** the arriving document does not say what it was built on */
  NoAncestryKnown = 'no-ancestry-known',
  /** it was built on another branch and nothing this device remembers is on both */
  NoCommonAncestor = 'no-common-ancestor',
}

/**
 * Choose the version a pull-side three-way merge of a singleton starts from (#6404).
 *
 * The merge is only right when it starts from a version BOTH sides were built on.
 * This device's latest remembered body is that version for an arrival built on it,
 * which is the ordinary case. It is not for an arrival built on a competing branch:
 * two devices saved the same document from one earlier version, and the arrival
 * descends from the other device's save. Measured against this device's own latest,
 * everything this device saved there reads as "unchanged here" and is handed to the
 * other branch. So when the arrival says what it was built on, the starting version
 * is the nearest remembered body that appears in that list.
 *
 * `base` is undefined when the caller must not merge at all.
 *
 * - No ancestry known: exactly the rule before #6404, a later generation merges
 *   against the latest body and a sibling does not merge.
 * - No common ancestor: the arrival is known to be from another branch and nothing
 *   remembered is on both. A sibling does not merge. A later generation still
 *   merges against the latest body, because not merging would replace the shared
 *   object outright and lose this device's unsaved edits as well (#4577). That
 *   merge loses what this device SAVED on its own branch, so the caller reports it.
 *
 * An earlier body is only the right starting version when it is where the two
 * branches really parted. Only a few bodies are kept, so the real parting point can
 * be one this device has let go: a later revision it once held, which the arrival
 * was ALSO built on. Starting from an older body then reads every edit the other
 * device made between the two as "unchanged there" and this device's stale value
 * wins, silently. `forgottenRevs` are the revisions of the bodies let go; if the
 * arrival was built on one that is nearer than the body found, the answer is "no
 * common ancestor", never the older body.
 */
export function chooseFoldBase<T extends { _rev?: string }>(args: {
  latest: T | null | undefined;
  previous: readonly T[];
  forgottenRevs: readonly string[];
  incomingRev: string | null | undefined;
  incomingAncestry: readonly string[] | null;
}): { base: T | undefined; choice: FoldBaseChoice } {
  const { latest, previous, forgottenRevs, incomingRev, incomingAncestry } = args;
  const latestIfLaterGeneration = latest != null && canFoldAgainstRememberedBase(latest._rev, incomingRev)
    ? latest
    : undefined;
  if (incomingAncestry == null) {
    return { base: latestIfLaterGeneration, choice: FoldBaseChoice.NoAncestryKnown };
  }
  if (latest?._rev && incomingAncestry.includes(latest._rev)) {
    return { base: latest, choice: FoldBaseChoice.OwnTip };
  }
  // for each earlier remembered body the arrival was built on: keep the nearest one,
  // which is the one of the highest generation
  let nearest: T | undefined;
  for (const candidate of previous) {
    if (!candidate?._rev || !incomingAncestry.includes(candidate._rev)) continue;
    if (!nearest || parseRevGeneration(candidate._rev) > parseRevGeneration(nearest._rev)) {
      nearest = candidate;
    }
  }
  // if the branches parted at a revision this device no longer has the body of, the
  // body found is too old to merge from
  const nearestGeneration = nearest ? parseRevGeneration(nearest._rev) : -1;
  const partedLater = forgottenRevs.some(rev =>
    parseRevGeneration(rev) > nearestGeneration && incomingAncestry.includes(rev));
  if (nearest && !partedLater) {
    return { base: nearest, choice: FoldBaseChoice.OlderCommonAncestor };
  }
  return { base: latestIfLaterGeneration, choice: FoldBaseChoice.NoCommonAncestor };
}

/** how many revisions of bodies it has let go a singleton keeps, to notice a too-old starting version */
export const MAX_FORGOTTEN_BODY_REVS = 100;

/**
 * Add the revisions of bodies a singleton has just let go to the ones it already
 * knows it let go, newest first, capped. Revision strings only, so this is cheap.
 * See `chooseFoldBase` for what they are for.
 */
export function rememberForgottenRevs(
  forgotten: readonly string[],
  letGo: readonly (string | undefined)[]
): string[] {
  const added = letGo.filter((rev): rev is string => !!rev && !forgotten.includes(rev));
  return [...added, ...forgotten].slice(0, MAX_FORGOTTEN_BODY_REVS);
}

/** Where this device got a database body it remembers for a singleton. */
export enum RememberedBodySource {
  /** it arrived from the server, or was read at start-up */
  Server = 'server',
  /** this device saved it */
  OwnSave = 'own-save',
}

/** One earlier database body a singleton remembers, and where this device got it. */
export interface RememberedDatabaseBody<T> {
  body: T;
  source: RememberedBodySource;
}

/** how many earlier bodies that came from the server a singleton keeps */
export const MAX_REMEMBERED_SERVER_BODIES = 2;
/** how many earlier bodies this device saved itself a singleton keeps */
export const MAX_REMEMBERED_SAVED_BODIES = 3;

/**
 * Add the body a singleton is about to stop calling its latest to the earlier bodies
 * it remembers, newest first, and drop what no longer fits (#6404).
 *
 * The two kinds are capped separately on purpose. The version two devices both
 * started from is one the SERVER held, so the bodies that came from the server are
 * the likeliest starting versions. A device working offline saves the same document
 * many times in a row, and with one shared cap those saves would push the last
 * server version out exactly when it is needed.
 *
 * Returns a new array; an outgoing body with no revision, or one already
 * remembered, changes nothing.
 */
export function rememberPreviousDatabaseBody<T extends { _rev?: string }>(
  previous: readonly RememberedDatabaseBody<T>[],
  outgoing: RememberedDatabaseBody<T> | null | undefined
): RememberedDatabaseBody<T>[] {
  const outgoingRev = outgoing?.body?._rev;
  if (!outgoing || !outgoingRev || previous.some(entry => entry.body._rev === outgoingRev)) {
    return [...previous];
  }
  const kept: RememberedDatabaseBody<T>[] = [];
  let serverCount = 0;
  let savedCount = 0;
  // for each entry, newest first: keep it while its own kind still has room
  for (const entry of [outgoing, ...previous]) {
    if (entry.source === RememberedBodySource.Server) {
      if (serverCount >= MAX_REMEMBERED_SERVER_BODIES) continue;
      serverCount++;
    } else {
      if (savedCount >= MAX_REMEMBERED_SAVED_BODIES) continue;
      savedCount++;
    }
    kept.push(entry);
  }
  return kept;
}

/**
 * What the identity map is about to do with a document a caller handed in, when
 * a shared object for that id already exists (#4577).
 */
export enum CanonicalUpdateAction {
  /** copy the incoming content over the shared object — what the app always does */
  Overwrite = 'overwrite',
  /**
   * the incoming copy is from the PAST, so the overwrite is about to destroy
   * newer content. The app still overwrites; this only says it happened.
   */
  OverwriteWithStaleCopy = 'overwrite-with-stale-copy',
  /**
   * the incoming body came from the DATABASE and is from the PAST, so it is
   * turned away and the shared object keeps what it holds (#4860).
   */
  KeepNewerSharedContent = 'keep-newer-shared-content',
  /**
   * the incoming body came from the DATABASE and is a SIBLING of what the shared
   * object holds — the same generation with a different hash, which is two
   * branches that disagree. It is turned away and the shared object keeps its own
   * branch (#5045 canary, 2026-09-25).
   */
  KeepSharedContentOverSibling = 'keep-shared-content-over-sibling',
  /**
   * the incoming body came from the DATABASE at the very same revision the shared
   * object carries. That is the body the shared object started from, so it holds
   * nothing new, and copying it over could only remove edits a page has not saved
   * yet. The shared object keeps what it holds (mh-physique#5904).
   */
  KeepSharedContentAtSameRevision = 'keep-shared-content-at-same-revision',
}

/**
 * What the identity map does when a body from the DATABASE is a sibling of the
 * shared object — the same generation, a different hash.
 */
export enum SiblingRevisionPolicy {
  /**
   * keep the shared object's own branch. The default, and the answer for every
   * body that has not been merged with this device's copy — every ordinary
   * document, and a singleton on any read that did not fold it: the branch this
   * device holds is the one it has been writing to, and moving sideways to the
   * other branch throws the device's latest edits out of view while nothing has
   * merged them.
   */
  KeepSharedContent = 'keep-shared-content',
  /**
   * apply the incoming body. Only for a singleton arriving on the pull path whose
   * version really WAS folded into the local one by the three-way merge of #4577
   * before it reaches the identity map — so the body being applied IS the merge
   * of both branches, and keeping only this device's side would throw the other
   * side away. `siblingPolicyForArrival` is the one place that picks it.
   */
  ApplyIncoming = 'apply-incoming',
}

/**
 * Which sibling policy a body from the database gets, given whether it was
 * merged with this device's copy before reaching the identity map
 * (df-f8cf38ad9fa8).
 *
 * Being a singleton is not enough on its own. A plain read of a singleton never
 * folds, and the pull path hands the arriving body back unmerged when it has no
 * remembered starting version or the merge could not use one. Such a body carries
 * only the other branch, so applying it would throw this device's own branch out
 * of view exactly as it would for an ordinary document (mh-physique#5045).
 *
 * @param wasFolded whether the body is the result of a real three-way merge with
 *   this device's copy
 */
export function siblingPolicyForArrival(wasFolded: boolean): SiblingRevisionPolicy {
  return wasFolded ? SiblingRevisionPolicy.ApplyIncoming : SiblingRevisionPolicy.KeepSharedContent;
}

/**
 * Did the identity map keep the shared object's own content instead of applying
 * the incoming body? Each of these answers says so: a body from the past (#4860),
 * a sibling branch, and the very same revision read back (#5904). Every reader
 * that acts on "the shared object did not change" asks this rather than naming
 * one of them, so a newly added answer cannot be missed the way a hand-written
 * `===` would miss it.
 */
export function isSharedContentKept(action: CanonicalUpdateAction | undefined): boolean {
  return action === CanonicalUpdateAction.KeepNewerSharedContent
    || action === CanonicalUpdateAction.KeepSharedContentOverSibling
    || action === CanonicalUpdateAction.KeepSharedContentAtSameRevision;
}

/**
 * Spot the case where a copy a caller handed to the identity map is older than
 * the shared object it is about to overwrite (#4577).
 *
 * # What this does and does NOT do
 *
 * It detects; it does not prevent. The app keeps one shared object per document
 * so every page reads the same thing, and when a caller hands back a copy it
 * made earlier, that copy's content is written over the shared one. If the copy
 * is out of date, newer content is destroyed — the same shape of loss as the
 * incident behind #4577, through a different door.
 *
 * **Repairing it is not possible from here, and that is a measured conclusion
 * rather than a shrug.** Two designs were built and both failed review:
 *
 * - Merging the two versions loses data whichever way it runs. With only two
 *   versions there is no way to tell "the other side never touched this field"
 *   from "the other side is holding the old value", so one side's edits are lost
 *   either way; and a two-way merge cannot express a deletion at all, so a list
 *   item the user removed and a field they deleted both come back.
 * - Having the wrapper remember the version a copy descended from cannot work
 *   after the fact either, because pages edit the shared object IN PLACE and the
 *   wrapper never sees those edits.
 *
 * So this reports, and the frequency it reports is what decides whether the real
 * fix — capturing the starting version at the moment a copy is made — is worth
 * building. See the deferred finding named in `design-docs/sync-integrity.md`.
 *
 * # The conditions, and why each one is there
 *
 * Report ONLY when every one of these holds. Anything else is an ordinary
 * update, and reporting those would bury the case that matters:
 *
 * - **Both sides carry a revision.** A missing revision means the caller is
 *   AUTHORING a document rather than carrying a copy of one — registering a
 *   workout that has never been saved, or creating a food over a previously
 *   deleted one. Those are correct and must stay quiet.
 * - **The incoming generation is strictly lower.** Equal generations with
 *   different hashes are branches that disagree, with no order between them, so
 *   neither is behind the other and neither is "from the past". What happens to
 *   such a sibling is its own rule, below, and it is never counted here.
 * - **The shared object is not a deletion marker.** A body arriving over a
 *   tombstone is a re-creation, not an overwrite of live content.
 *
 * # One of the two doors IS closed, and only one (#4860)
 *
 * Everything above is about a CALLER handing back a copy it has been editing, and
 * that door stays exactly as it was. The other door is a body the DATABASE itself
 * produced - a row of a read, or a version arriving from the server - and there the
 * answer is not a judgement call at all: a database body from an EARLIER generation
 * than the one this device already holds cannot be the truth, because this device's
 * copy descends from it. So it is turned away and the shared object keeps what it
 * has. (One body genuinely does not descend from it — a document deleted and made
 * again starts a fresh chain at generation 1 — and that one is turned away too when
 * this device never saw the deletion. `design-docs/sync-integrity.md` says why that
 * is accepted and why the obvious carve-out for it would reopen this bug.)
 *
 * That is not a new rule in this app, only a rule the read side did not follow yet.
 * `applyPulledDocToCache` has always ignored an arriving revision that is not at
 * least the generation it expected, calling it the expected echo of this device's
 * own push.
 *
 * The case it was written for: while the first sync of a session had not finished,
 * every read was served by the SERVER rather than by the phone's own database (see
 * `chooseReadDb` and `design-docs/pass-through-db.md`). A phone that still had changes
 * it had not managed to push therefore read OLDER versions of its own documents back.
 * A customer who had finished a workout was told the next morning that it was
 * unfinished, because the server's copy of it - 22 versions behind his phone's - was
 * copied over the shared object his home page was reading.
 *
 * #5068 has since closed that window for the user's own database on a device that has
 * downloaded it here, so that particular route is gone on the common path. The rule
 * below stays, because it guards every other way an older database body can arrive -
 * a first-ever sync, a reset local store, and every shared-database read.
 *
 * # A SIBLING from the database is turned away too (2026-09-25)
 *
 * The same generation with a different hash is two branches of one document that
 * disagree, and neither is behind the other. For a body a CALLER hands in that is
 * still an ordinary overwrite, exactly as before. For a body the DATABASE produced
 * it is not: a read answers with whichever branch CouchDB's tie-break makes the
 * winner, and copying that branch over the shared object throws this device's own
 * latest writes out of view while nothing has merged them. That is how the canary
 * lost a food lock the user had just set (mh-physique#5045): the lock was saved
 * onto the device's branch, the next read handed back the other branch, and the
 * lock vanished from the screen and from every cache. So the shared object keeps
 * its own branch and keeps its own revision, and the next save extends that branch;
 * the on-device conflict resolver is what merges the two, and it does so by
 * writing a NEW revision above the winner, which is a strictly newer generation and
 * is applied as one.
 *
 * The one exception is a singleton arriving on the pull path whose version really
 * was folded into the local one by the three-way merge of #4577 before it reaches
 * this decision (`siblingPolicy` says so): the body being applied carries both
 * sides, and keeping only this device's side would throw the other side away. A
 * singleton body that was NOT folded — a plain read, or a pull with no usable
 * starting version — is kept out like any other (df-f8cf38ad9fa8). The fold merges a
 * sibling only from an EARLIER remembered body the arrival was built on
 * (`chooseFoldBase` above, #6404), never from this device's own latest body, which a
 * sibling was not built on.
 *
 * # The very same revision from the database is kept out as well (#5904)
 *
 * A database body carrying exactly the revision the shared object carries is the
 * body the shared object started from, so it holds nothing new. Copying it over
 * could only remove what a page changed and has not saved yet — every `await`
 * between an edit and its save is a window for a background range read to land in.
 * That is how the #5045 canary lost a food lock a second way: the Lock Food tap set
 * the lock in memory, a day-status read landed before the save, and the save wrote
 * the unlocked meal. The pull path has always ignored this case as the echo of the
 * device's own push; this is the read side following the same rule. A caller's
 * copy at the same revision (the meal-plan page's save) still overwrites, and so
 * does anything arriving over a tombstone. See design-docs/sync-integrity.md, "The
 * opposite direction", for what this gives up.
 *
 * @param cachedRev the revision on the shared object the app already holds
 * @param incomingRev the revision on the copy being handed in
 * @param cachedIsDeleted whether the shared object is a deletion marker
 * @param sourceIsDatabaseBody whether the incoming body came from the DATABASE (a
 *   read, or a version arriving from the server) rather than from a caller holding
 *   a copy. Defaults to false, which is the answer that changes nothing - a caller
 *   that has not thought about it keeps the behaviour this function always had.
 * @param siblingPolicy what to do with a database body that is a sibling of the
 *   shared object. Defaults to keeping the shared object; only a folded singleton
 *   arrival passes `ApplyIncoming`. Ignored for a caller's copy, which always
 *   overwrites.
 */
export function decideCanonicalUpdateAction(
  cachedRev: string | null | undefined,
  incomingRev: string | null | undefined,
  cachedIsDeleted: boolean,
  sourceIsDatabaseBody: boolean = false,
  siblingPolicy: SiblingRevisionPolicy = SiblingRevisionPolicy.KeepSharedContent
): CanonicalUpdateAction {
  if (!cachedRev || !incomingRev) return CanonicalUpdateAction.Overwrite;
  if (cachedIsDeleted) return CanonicalUpdateAction.Overwrite;
  const incomingGeneration = parseRevGeneration(incomingRev);
  const cachedGeneration = parseRevGeneration(cachedRev);
  if (incomingGeneration < cachedGeneration) {
    return sourceIsDatabaseBody
      ? CanonicalUpdateAction.KeepNewerSharedContent
      : CanonicalUpdateAction.OverwriteWithStaleCopy;
  }
  // if the database hands back the very revision the shared object carries, it is
  // the body the shared object started from: keep what we hold, which may include
  // edits not saved yet (#5904). Not a sibling, so the sibling report stays quiet.
  if (sourceIsDatabaseBody && incomingRev === cachedRev) {
    return CanonicalUpdateAction.KeepSharedContentAtSameRevision;
  }
  const isSibling = incomingGeneration === cachedGeneration && incomingRev !== cachedRev;
  if (isSibling && sourceIsDatabaseBody && siblingPolicy === SiblingRevisionPolicy.KeepSharedContent) {
    return CanonicalUpdateAction.KeepSharedContentOverSibling;
  }
  return CanonicalUpdateAction.Overwrite;
}

/**
 * Is this answer one of the two that mean "the incoming body is from the past"?
 *
 * Asked so the report fires for BOTH of them. The two differ in what the app then
 * does - one overwrites, one keeps what it holds - but a body from the past is
 * worth counting either way, and the report is how the rate of each stays visible.
 */
export function isOutOfDateCopy(action: CanonicalUpdateAction): boolean {
  return action === CanonicalUpdateAction.OverwriteWithStaleCopy
    || action === CanonicalUpdateAction.KeepNewerSharedContent;
}

/**
 * Did an out-of-date body handed to the identity map actually destroy anything? (#4669)
 *
 * # Why this question needs asking at all
 *
 * `decideCanonicalUpdateAction` above reports whenever the incoming body is from an
 * EARLIER revision than the shared object. It compares revision NUMBERS and nothing
 * else, so it cannot tell a real loss from a harmless one — and harmless ones exist.
 * `DatabaseWrapper.updateDocumentInPlace` copies the incoming body's fields over the
 * shared object but leaves every key starting with `_` or `db__` alone, so two
 * revisions differing only in the save-time stamp are, as far as that overwrite is
 * concerned, the same document: it copies identical values over identical values and
 * destroys nothing. The report still fires.
 *
 * That matters for READING the report rather than for what the app does. Production
 * showed it firing for about one in three daily users, and a count that mixes real
 * losses in with no-ops overstates the problem by an unknown amount. This says how
 * much of it is real.
 *
 * # Why `deepCompareEQ` is the right comparator and not merely a convenient one
 *
 * At the TOP level it skips exactly the keys the overwrite preserves — `_` and `db__` —
 * so it already answers the question about the same set of fields the overwrite would
 * copy. Using it here rather than writing that rule out a second time is what stops the
 * two drifting apart, which would be silent: the comparison would go on answering
 * confidently about a different set of fields than the one being copied.
 *
 * **Below the top level the two rules part company, and the answer can be too small.**
 * `deepCompareEQ` re-applies the `_`/`db__` skip at every depth, while
 * `deepOverwriteCouchSafe` applies it to top-level keys only and deep-clones a nested
 * object whole. So a nested `_`-prefixed field that genuinely differs is copied by the
 * overwrite and reported here as no change. It can only ever under-count, never
 * over-count, and no document we hold is known to carry a nested `_` field — but a
 * reader judging how much of the count is real should know the bound is one-sided.
 *
 * # Two things to know before changing it
 *
 * It walks the whole document, so it is called ONLY on the report path, which is
 * already the rare branch. It must not be added to an ordinary read.
 *
 * A body that is missing entirely answers "yes, something changed". There is nothing
 * to compare, and claiming nothing was lost is the answer that would quietly shrink
 * the count.
 */
export function staleCopyWouldChangeContent(cached: any, incoming: any): boolean {
  if (!cached || !incoming) return true;
  return !deepCompareEQ(cached, incoming);
}

/**
 * How far behind was the out-of-date body, as a word a Sentry tag may carry? (#4669)
 *
 * The distance separates the two causes this report cannot otherwise tell apart. A
 * read that raced a save is exactly ONE generation behind — the save moved the shared
 * object on while the read was in flight. A copy somebody has been editing drifts
 * further behind the longer they hold it. In production 83 of 100 sampled events were
 * one generation behind, which is what pointed the investigation at the read paths.
 *
 * The answer is bucketed rather than exact because a Sentry tag is an index: it has
 * to stay low-cardinality, and the difference between "9 behind" and "11 behind" is
 * not a difference anybody would act on.
 *
 * "I could not work it out" is its OWN answer and never a number. A missing or
 * unparseable revision counted as generation zero would be reported as a huge
 * distance, which is a wrong measurement rather than a missing one.
 */
export function describeRevGap(cachedRev: string | null | undefined, incomingRev: string | null | undefined): string {
  if (!cachedRev || !incomingRev) return 'unknown';
  const cachedGeneration = parseRevGeneration(cachedRev);
  const incomingGeneration = parseRevGeneration(incomingRev);
  if (cachedGeneration <= 0 || incomingGeneration <= 0) return 'unknown';
  const gap = cachedGeneration - incomingGeneration;
  if (gap <= 0) return 'unknown';
  if (gap === 1) return '1';
  if (gap === 2) return '2';
  if (gap <= 5) return '3-5';
  return '6+';
}

/**
 * Is it worth comparing the shared object against the last body the database
 * gave us, before a database-sourced body is copied over it? (#4802's finding)
 *
 * # What this is for
 *
 * The app keeps one shared JavaScript object per document, and a body coming
 * from the database — pulled from the server, or read back off the disk — is
 * copied straight over it. Anything a page had changed and not yet saved is
 * destroyed at that moment, and until now nothing said so anywhere.
 *
 * `decideCanonicalUpdateAction` above does NOT cover this. It fires only when
 * the incoming copy is provably from the PAST, which is the opposite direction:
 * there a stale copy destroys good content. Here the incoming body is the newest
 * one the database has, and it is the app's own unsaved work that goes.
 *
 * Nothing about the overwrite changes — this only decides whether to look.
 * `design-docs/sync-integrity.md` is explicit that a measurement which quietly
 * altered what it measures would be worthless.
 *
 * # Why it is a separate question from the comparison itself
 *
 * Working out whether the shared object still matches the database means walking
 * the whole document. That is not free, and it sits on the path every view read
 * takes. So the cheap questions are all asked first, here, and the walk only
 * happens for a document that has passed every one of them.
 *
 * # The conditions, and why each one is there
 *
 * - **We remember what the database last held.** Without that there is nothing
 *   to compare against, so a difference cannot be attributed to anybody.
 * - **The document is not a singleton.** Those roughly dozen documents already
 *   have the real fix: an arriving version is folded into what this device holds
 *   rather than replacing it (#4577), and that fold does its own reporting.
 *   Asking here as well would report a loss that did not happen.
 * - **The shared object is not a deletion marker.** A body arriving over a
 *   tombstone is a re-creation, not an overwrite of live content — the same
 *   reason `decideCanonicalUpdateAction` excludes it.
 * - **This document has not already been reported this session.** The condition
 *   is common — one workout scenario in CI trips the workout page's own version
 *   of this report two to four times — and what the number is for is how many
 *   documents this happens to, not how many times over. Repeating it per
 *   document would bury that in noise.
 * - **This session has not already reported its fill of documents.** The set of
 *   documents already reported has to be remembered to answer the previous
 *   condition, and an unbounded one is a slow leak in a long session. A session
 *   that has already produced this many is not made clearer by more.
 *
 * @param rememberedFingerprint fingerprint of the last body the database gave us
 *   for this document, or undefined if we have never seen one
 */
export function shouldCompareCanonicalAgainstDatabaseBody(args: {
  rememberedFingerprint: string | undefined;
  isSingleton: boolean;
  cachedIsDeleted: boolean;
  alreadyReportedThisSession: boolean;
  reportedDocumentCount: number;
  maxReportedDocuments: number;
}): boolean {
  if (!args.rememberedFingerprint) return false;
  return isDocumentWatchedForUnsavedContent(args);
}

/**
 * Could this document ever produce the report described above? (#4802's finding)
 *
 * Working out the fingerprint means walking the whole document, so it is worth
 * asking this before doing it — for a document that can never be reported, the
 * walk buys nothing and never will.
 *
 * **The two sides must ask the SAME question, which is why this is one function
 * rather than two lists that happen to agree today.** A document is only ever
 * compared against a fingerprint recorded under this same rule, so if the
 * recording side were stricter than the comparing side, a document would be
 * compared against a fingerprint from further back than the caller believes —
 * and the difference would be reported as work somebody lost when it was in fact
 * saved. A false report of data loss is worse than no report.
 *
 * @param isSingleton whether this document is one of the singletons — those
 *   already have the real fix (#4577) and report for themselves
 * @param cachedIsDeleted whether the shared object is a deletion marker, in
 *   which case a body arriving over it is a re-creation rather than a loss
 * @param alreadyReportedThisSession whether this document has already been
 *   reported once since the app started
 * @param reportedDocumentCount how many distinct documents have been reported
 *   since the app started
 * @param maxReportedDocuments the most documents one session may report
 */
export function isDocumentWatchedForUnsavedContent(args: {
  isSingleton: boolean;
  cachedIsDeleted: boolean;
  alreadyReportedThisSession: boolean;
  reportedDocumentCount: number;
  maxReportedDocuments: number;
}): boolean {
  if (args.isSingleton) return false;
  if (args.cachedIsDeleted) return false;
  if (args.alreadyReportedThisSession) return false;
  if (args.reportedDocumentCount >= args.maxReportedDocuments) return false;
  return true;
}

/**
 * Should the backlog cache report that it is about to stamp a disk revision onto its
 * own copy of an entry? (#4835)
 *
 * # What is being decided
 *
 * `mergeLogEntries` has one direction that keeps the backlog cache's body while taking
 * the revision of the row just read from disk. Both are needed for the merge to work,
 * but together they are exactly the shape no revision-comparing check can see: after
 * the stamp the two revisions are equal, so `decideCanonicalUpdateAction` answers
 * `Overwrite` for a body that may be many generations behind, and the older content is
 * copied over the shared object with nothing written down anywhere.
 *
 * # Why the only real condition is "these are two different objects"
 *
 * Every row the disk read produces has already been through the identity map, so it IS
 * the shared object for its id. When the backlog cache is holding that same shared
 * object — which is the invariant the cache states for itself — the two sides are the
 * same object, the stamp assigns a revision to itself, and the canonicalising call
 * afterwards returns early. Nothing happens, and there is nothing to report.
 *
 * So a difference between the two objects is not merely the trigger, it is the whole
 * condition. It means the cache is holding an object for an id that some other object
 * now owns, and that is the only state in which this line can destroy anything.
 *
 * # Why whether the bodies differ is NOT asked here
 *
 * It is carried as a tag on the report instead, the same way
 * `identity_map_content_differs` is carried on the identity-map report. The two answers
 * settle different questions, and a reader needs both: "the cache holds objects it
 * should not" and "those objects are destroying content". Gating on the second would
 * make the first unanswerable, and the first is the one this report was built to ask.
 *
 * @param isSameObject whether the cache's entry and the disk row are the same object,
 *   which is the ordinary, invariant-holding case
 * @param alreadyReportedThisSession whether this document has already been reported
 *   once since the app started
 * @param reportedDocumentCount how many distinct documents have been reported since the
 *   app started
 * @param maxReportedDocuments the most documents one session may report
 */
export function shouldReportBacklogRevStamp(args: {
  isSameObject: boolean;
  alreadyReportedThisSession: boolean;
  reportedDocumentCount: number;
  maxReportedDocuments: number;
}): boolean {
  if (args.isSameObject) return false;
  if (args.alreadyReportedThisSession) return false;
  if (args.reportedDocumentCount >= args.maxReportedDocuments) return false;
  return true;
}

/**
 * How the revision on the body being applied stands against the one the shared
 * object is carrying. Reported alongside the loss so the two shapes behind it
 * can be counted apart (#4802's finding).
 */
export enum IncomingRevRelation {
  /** the same revision — so the difference is entirely this device's own doing */
  Same = 'same',
  /** a later generation — this is the shape the deferred finding describes */
  Newer = 'newer',
  /** an earlier generation — `decideCanonicalUpdateAction` reports this one too */
  Older = 'older',
  /** one of the two revisions is missing, so they cannot be compared */
  Unknown = 'unknown',
}

/**
 * Say how an incoming revision stands against the one already held, for the
 * report described on `shouldCompareCanonicalAgainstDatabaseBody`.
 *
 * This exists to keep the two shapes tellable apart in the numbers. `Newer` is
 * the deferred finding's own headline: a version arriving from the server wins
 * on revision number and takes the app's unsaved work with it. `Same` is the
 * quieter one — the same stored revision read back over the shared object, which
 * cannot bring anything new and can only ever remove.
 */
export function describeIncomingRevRelation(
  cachedRev: string | null | undefined,
  incomingRev: string | null | undefined
): IncomingRevRelation {
  if (!cachedRev || !incomingRev) return IncomingRevRelation.Unknown;
  if (cachedRev === incomingRev) return IncomingRevRelation.Same;
  const cachedGeneration = parseRevGeneration(cachedRev);
  const incomingGeneration = parseRevGeneration(incomingRev);
  if (incomingGeneration > cachedGeneration) return IncomingRevRelation.Newer;
  if (incomingGeneration < cachedGeneration) return IncomingRevRelation.Older;
  // same generation, different hash: two branches that disagree, with no order
  // between them. Counting that as "newer" would overstate the finding's shape.
  return IncomingRevRelation.Unknown;
}

/** How loudly the result of a three-way merge should be reported (#4577). */
export enum MergeReportTier {
  /** nothing this device did was at stake — say nothing, this is the ordinary case */
  Nothing = 'nothing',
  /** every difference had exactly one author, so nothing was guessed and nothing was lost */
  Info = 'info',
  /** at least one value was picked by a rule rather than known to be right */
  Problem = 'problem',
}

/**
 * Decide how loudly to report what a three-way merge had to do (#4577).
 *
 * `mergedWithoutBase` counts as a problem because it means there was no shared
 * starting point to reason from, so the whole outcome was settled by rule rather
 * than by knowing who changed what.
 *
 * Callers that reach the merge on an ordinary racing save — where the two-way
 * fallback has always been the normal, correct answer — must not report at all;
 * this decides the tier for a merge that only happened because something was
 * wrong, not whether to report in the first place.
 */
export function decideMergeReportTier(result: {
  guessedFieldCount: number;
  localOnlyChangeCount: number;
  mergedWithoutBase: boolean;
}): MergeReportTier {
  if (result.mergedWithoutBase || result.guessedFieldCount > 0) {
    return MergeReportTier.Problem;
  }
  if (result.localOnlyChangeCount > 0) {
    return MergeReportTier.Info;
  }
  return MergeReportTier.Nothing;
}
